import React from 'react';
import { ChatMessage } from '../../stores/useChatStore.js';
import IntelligentResponseCard, {
    type IntelligentResponseData,
    type ContentFormat,
} from './IntelligentResponseCard.js';

interface AssistantResponseCardProps {
    message: ChatMessage;
    showProvenance?: boolean;
}

const resolveVerificationArtifact = (envelope?: {
    artifacts?: Record<string, string | null | undefined>;
    artifactPointers?:
        | string[]
        | {
              plan?: string;
              trace?: string;
              verification?: string;
              ledger?: string;
          };
}): string | undefined => {
    if (!envelope || typeof envelope !== 'object') return undefined;
    const artifacts = envelope.artifacts;
    if (artifacts?.verification) return artifacts.verification;
    const artifactPointers = envelope.artifactPointers;
    if (Array.isArray(artifactPointers)) {
        const match = artifactPointers.find(pointer => pointer.includes('verification'));
        return match;
    }
    if (
        artifactPointers &&
        typeof artifactPointers === 'object' &&
        !Array.isArray(artifactPointers)
    ) {
        const verification = (artifactPointers as { verification?: string }).verification;
        return typeof verification === 'string' ? verification : undefined;
    }
    return undefined;
};

const buildResponseData = (message: ChatMessage): IntelligentResponseData => {
    const executionEnvelope = message.metadata?.executionEnvelope as
        | {
              verdict?:
                  | 'SUCCESS'
                  | 'FAILED'
                  | 'INCOMPLETE'
                  | 'BLOCKED'
                  | 'DENIED'
                  | 'NOT_APPLICABLE';
              artifacts?: {
                  plan?: string;
                  trace?: string;
                  verification?: string;
                  ledger?: string;
              };
              artifactPointers?:
                  | string[]
                  | {
                        plan?: string;
                        trace?: string;
                        verification?: string;
                        ledger?: string;
                    };
              reasonCode?: string;
              intentName?: string;
              intentClass?: string;
              nextActions?: Array<{
                  label: string;
                  type: 'button' | 'link' | 'suggestion';
                  payload: string;
              }>;
          }
        | undefined;

    const verdict = executionEnvelope?.verdict;
    const verificationArtifact = resolveVerificationArtifact(executionEnvelope);
    const artifactPointers = executionEnvelope?.artifactPointers;
    const artifacts =
        executionEnvelope?.artifacts ??
        (artifactPointers && !Array.isArray(artifactPointers)
            ? (artifactPointers as IntelligentResponseData['artifacts'])
            : Array.isArray(artifactPointers)
              ? (artifactPointers.reduce<Record<string, string>>((acc, value, index) => {
                    acc[`artifact-${index + 1}`] = value;
                    return acc;
                }, {}) as IntelligentResponseData['artifacts'])
              : undefined);

    return {
        answer: message.content,
        // Default to 'plain' to ensure deterministic rendering - never allow undefined
        contentFormat: (message.metadata?.contentFormat as ContentFormat) ?? 'plain',
        confidence: message.metadata?.confidence as number | undefined,
        model: message.metadata?.model as string | undefined,
        traceId: message.metadata?.traceId as string | undefined,
        verdict,
        hasVerificationArtifacts: Boolean(verificationArtifact),
        reasonCode: executionEnvelope?.reasonCode,
        verified: !verdict && (message.metadata?.verified as boolean | undefined),
        intentName:
            executionEnvelope?.intentName ?? (message.metadata?.intentName as string | undefined),
        timestamp: new Date(message.timestamp),
        artifacts,
        nextActions: executionEnvelope?.nextActions,
    };
};

const AssistantResponseCard: React.FC<AssistantResponseCardProps> = ({
    message,
    showProvenance = true,
}) => {
    return (
        <IntelligentResponseCard
            data={buildResponseData(message)}
            showProvenance={showProvenance}
        />
    );
};

export default AssistantResponseCard;
